import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Create auth context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingToken, setRefreshingToken] = useState(false);
  const [passwordChallengeData, setPasswordChallengeData] = useState(null);

  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');
  
  // Function to refresh tokens
  const refreshTokens = async () => {
    try {
      // Prevent multiple simultaneous refresh attempts
      if (refreshingToken) return false;
      
      setRefreshingToken(true);
      console.log('Attempting to refresh token');
      
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        console.error('No refresh token available');
        return false;
      }
      
      const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
      const { tokens } = response.data;
      
      if (!tokens) {
        console.error('No tokens returned from refresh endpoint');
        return false;
      }
      
      console.log('Token refreshed successfully');
      
      // Update tokens in localStorage
      localStorage.setItem('token', tokens.idToken || tokens.accessToken);
      
      // Update auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.idToken || tokens.accessToken}`;
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      setRefreshingToken(false);
    }
  };

  // Setup Axios interceptor for token refresh
  useEffect(() => {
    // Create response interceptor to handle 401 errors
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        // If the error is 401 (Unauthorized) and we haven't already tried to refresh
        if (error.response && 
            error.response.status === 401 && 
            !originalRequest._retry && 
            currentUser) { // Only try to refresh if we have a logged in user
          
          console.log('Received 401 error, attempting to refresh token');
          originalRequest._retry = true;
          
          // Try to refresh the token
          const refreshed = await refreshTokens();
          
          if (refreshed) {
            // Update the authorization header in the original request
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
            
            // Retry the original request with the new token
            return axios(originalRequest);
          } else {
            // If refresh failed, logout the user
            console.log('Token refresh failed, logging out');
            await logout();
            return Promise.reject(error);
          }
        }
        
        // For other errors, just reject the promise
        return Promise.reject(error);
      }
    );
    
    // Clean up interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [currentUser]); // Re-setup when currentUser changes
  
  // Initialize auth state - Check if user is already logged in
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        
        if (storedUser && storedToken) {
          // Set auth header for all future requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Set the current user
          setCurrentUser(JSON.parse(storedUser));
          
          console.log('User restored from local storage');
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        // Clear potentially corrupted auth data
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Request registration function
  const requestRegistration = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the backend registration request endpoint
      const response = await axios.post(`${API_URL}/auth/request-registration`, {
        email,
        password
      });
      
      return response.data;
    } catch (err) {
      console.error('Registration request error:', err);
      
      // Check for specific error messages from the backend
      if (err.response && err.response.data) {
        console.error('Backend error details:', err.response.data);
        setError(err.response.data.message || err.response.data.error || 'Registration request failed.');
      } else {
        setError('Registration request failed. Network error or server unavailable.');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Complete new password challenge for Cognito
  const completeNewPasswordChallenge = async (newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!passwordChallengeData) {
        setError('No password challenge in progress');
        return null;
      }
      
      const { email, session } = passwordChallengeData;
      
      // Call the backend to complete the challenge
      const response = await axios.post(`${API_URL}/auth/complete-new-password`, {
        email,
        session,
        newPassword
      });
      
      const { user, tokens } = response.data;
      
      // If no user was returned but we have tokens (Cognito case)
      if (!user && tokens) {
        // Determine role based on email domain
        const domain = email.split('@')[1];
        const isAdmin = domain === 'opplane.com';
        const role = isAdmin ? 'admin' : 'client';
        
        // Create user object
        const cognitoUser = {
          id: email, // Use email as ID for Cognito users
          email: email,
          name: email.split('@')[0],
          role: role
        };
        
        // Store user info and tokens in localStorage
        localStorage.setItem('user', JSON.stringify(cognitoUser));
        localStorage.setItem('token', tokens.idToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Set auth header for all future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.idToken}`;
        
        // Update state
        setCurrentUser(cognitoUser);
        setPasswordChallengeData(null);
        
        return cognitoUser;
      }
      
      return null;
    } catch (err) {
      console.error('Complete new password challenge error:', err);
      
      // Check for specific error messages from the backend
      if (err.response && err.response.data) {
        console.error('Backend error details:', err.response.data);
        setError(err.response.data.message || err.response.data.error || 'Password change failed.');
      } else {
        setError('Password change failed. Network error or server unavailable.');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Login function with AWS Cognito integration
  const login = async (email, password) => {
    try {
      // Don't clear error state here, as it will cause the error to disappear
      // on subsequent login attempts
      setLoading(true);
      setError(null);
      
      // Validate email domain
      const domain = email.split('@')[1];
      if (!domain) {
        setError('Invalid email format');
        return null;
      }
      
      // Check if domain is allowed
      const isAdmin = domain === 'opplane.com';
      const isClient = domain === 'verizon.com' || domain === 'verizonwireless.com';
      
      if (!isAdmin && !isClient) {
        setError('Email domain not authorized. Only @opplane.com, @verizon.com, and @verizonwireless.com domains are allowed.');
        return null;
      }
      
      // Call the backend authentication endpoint
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      // Check if response contains a NEW_PASSWORD_REQUIRED challenge
      if (response.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('NEW_PASSWORD_REQUIRED challenge detected');
        
        // Store challenge data for the password change component
        setPasswordChallengeData({
          email,
          session: response.data.session
        });
        
        // Return a special object to indicate password change required
        return { passwordChangeRequired: true };
      }
      
      const { user, tokens } = response.data;

      // If no user was returned but we have tokens (Cognito case)
      if (!user && tokens) {
        // Determine role based on email domain
        const domain = email.split('@')[1];
        // Determine role based on email domain
        const isAdmin = domain === 'opplane.com';
        const role = isAdmin ? 'admin' : 'client';
        
        // Create user object
        const cognitoUser = {
          id: email, // Use email as ID for Cognito users
          email: email,
          name: email.split('@')[0],
          role: role
        };
        
        // Store user info and tokens in localStorage
        localStorage.setItem('user', JSON.stringify(cognitoUser));
        localStorage.setItem('token', tokens.idToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Set auth header for all future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.idToken}`;
        
        // Update state
        setCurrentUser(cognitoUser);
        
        return cognitoUser;
      } else if (user) {
        // Local authentication case (fallback)
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Set auth header for all future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
        
        // Update state
        setCurrentUser(user);
        
        return user;
      }
      
      return null;
    } catch (err) {
      console.error('Login error:', err);
      
      // Check for specific error messages from the backend
      if (err.response && err.response.data) {
        console.error('Backend error details:', err.response.data);
        setError(err.response.data.message || err.response.data.error || 'Login failed. Please check your credentials.');
      } else {
        setError('Login failed. Network error or server unavailable.');
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          await axios.post(`${API_URL}/auth/logout`, { refreshToken });
        } catch (err) {
          console.error('Logout API error:', err);
          // Continue with local logout even if API call fails
        }
      }
      
      // Clear local storage
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      // Clear auth header
      delete axios.defaults.headers.common['Authorization'];
      
      // Update state
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return currentUser?.role === 'admin' || currentUser?.groups?.includes('admin');
  };

  // Value object to be provided to consumers
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    isAdmin,
    refreshTokens,
    requestRegistration,
    completeNewPasswordChallenge,
    passwordChallengeData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
